import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaw,
  faCalendarAlt,
  faClock,
  faChild,
  faLeaf,
  faHorse,
  faStar,
  faTicketAlt,
  faDragon,
  faIceCream,
  faTree,
} from '@fortawesome/free-solid-svg-icons';
import ParallaxBanner from '../../common/Parralax-banner';
import Footer from '../../Footer/Footer';
import { useRetailSection } from '../../../hooks/useRetailSection';
import animalMap from '../../../assets/AnimalFarmMap.webp';
import animalBanner from '../../../assets/banner/AnimalBan.webp';
import ponyImage from '../../../assets/Pony.jpeg';
import SEO from '../../common/SEO';
import { getAnimalParkOpeningDisplay } from '../../../utils/openingHoursUtils';

const SUMMER_SAFARI_URL = '/belvoir-summer-safari';

// ─── Layout ────────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const Wrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
`;

// ─── Quick-info strip ──────────────────────────────────────────────────────────

const InfoStrip = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
`;

const InfoStripInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  divide-color: rgba(255,255,255,0.15);

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.4rem 1.5rem;
  border-right: 1px solid rgba(255, 255, 255, 0.12);

  &:last-child {
    border-right: none;
  }

  @media (max-width: 640px) {
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    &:last-child { border-bottom: none; }
  }
`;

const InfoIcon = styled.div`
  font-size: 1.4rem;
  color: ${props => props.theme.colors.accent};
  flex-shrink: 0;
  width: 2rem;
  text-align: center;
`;

const InfoText = styled.div`
  & strong {
    display: block;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    opacity: 0.65;
    margin-bottom: 0.15rem;
    font-weight: 600;
  }
  & span {
    font-size: 1rem;
    font-weight: 600;
  }
`;

// ─── Intro ─────────────────────────────────────────────────────────────────────

const IntroSection = styled.section`
  padding: 5rem 0 3rem;
  text-align: center;
`;

const EyebrowLabel = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 1rem;
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(2.2rem, 4vw, 3.2rem);
  color: ${props => props.theme.colors.primary};
  line-height: 1.2;
  margin-bottom: 1.5rem;
`;

const IntroBody = styled.p`
  font-size: 1.15rem;
  line-height: 1.85;
  color: ${props => props.theme.colors.secondary};
  max-width: 760px;
  margin: 0 auto 2.5rem;
`;

const BookBtn = styled.a`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  font-size: 1.05rem;
  font-weight: 700;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  text-decoration: none;
  border: 2px solid ${props => props.theme.colors.accent};
  transition: background-color 0.25s ease, color 0.25s ease, transform 0.25s ease,
    box-shadow 0.25s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);

  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`;

const IntroCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  align-items: center;
`;

const SecondaryBookBtn = styled.a`
  display: inline-block;
  background-color: transparent;
  color: ${props => props.theme.colors.primary};
  font-size: 1.05rem;
  font-weight: 700;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  text-decoration: none;
  border: 2px solid ${props => props.theme.colors.primary};
  transition: background-color 0.25s ease, color 0.25s ease, transform 0.25s ease;

  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-2px);
  }
`;

// ─── Summer Safari callout ─────────────────────────────────────────────────────

const SafariSection = styled.section`
  padding: 0 0 5rem;
`;

const SafariPanel = styled.div`
  background: ${props => props.theme.colors.primary};
  border-radius: 16px;
  padding: 3.5rem 3rem;
  color: white;

  @media (max-width: 768px) {
    padding: 2.5rem 1.75rem;
  }
`;

const SafariEyebrow = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.85rem;
`;

const SafariTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.9rem, 3.2vw, 2.6rem);
  color: white;
  line-height: 1.2;
  margin-bottom: 1rem;
`;

const SafariBody = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.85);
  max-width: 720px;
  margin-bottom: 2rem;
`;

const SafariIncludes = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2.25rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.85rem 1.5rem;
`;

const SafariInclude = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);

  & svg {
    color: ${props => props.theme.colors.accent};
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

// ─── Divider ───────────────────────────────────────────────────────────────────

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  margin: 0;
`;

// ─── Experiences section ───────────────────────────────────────────────────────

const ExperiencesSection = styled.section`
  padding: 5rem 0;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.75rem;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.secondary};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.7;
`;

const ExperiencesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
`;

const ExperienceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.25rem 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const CardIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background-color: ${props => props.theme.colors.primary}12;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.25rem;
`;

const CardTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.6rem;
`;

const CardText = styled.p`
  font-size: 1rem;
  line-height: 1.65;
  color: ${props => props.theme.colors.secondary};
  margin: 0;
`;

// ─── Pony ride days callout ────────────────────────────────────────────────────

const MayFourthSection = styled.section`
  padding: 5rem 0;
`;

const MayFourthInner = styled.div`
  background: ${props => props.theme.colors.primary};
  border-radius: 16px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 360px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MayFourthContent = styled.div`
  padding: 3.5rem 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 2.5rem 2rem;
  }
`;

const EventBadge = styled.span`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.35rem 0.9rem;
  border-radius: 50px;
  margin-bottom: 1.25rem;
`;

const MayFourthTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  color: white;
  line-height: 1.2;
  margin-bottom: 1rem;
`;

const MayFourthBody = styled.p`
  font-size: 1.05rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.82);
  margin-bottom: 2rem;
`;

const MayFourthDetails = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const MayFourthDetail = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.97rem;
  color: rgba(255, 255, 255, 0.9);

  & svg {
    color: ${props => props.theme.colors.accent};
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

const WhiteBookBtn = styled.a`
  display: inline-block;
  background-color: white;
  color: ${props => props.theme.colors.primary};
  font-size: 1rem;
  font-weight: 700;
  padding: 0.9rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  align-self: flex-start;
  border: 2px solid white;
  transition: background-color 0.25s ease, color 0.25s ease;

  &:hover {
    background-color: transparent;
    color: white;
  }
`;

const MayFourthVisual = styled.div`
  position: relative;
  min-height: 360px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);

  @media (max-width: 768px) {
    min-height: 260px;
  }
`;

const MayFourthImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
  display: block;
`;

// ─── Reptile Day ───────────────────────────────────────────────────────────────

const ReptileSection = styled.section`
  padding: 5rem 0;
`;

const ReptileCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const ReptileLogoBanner = styled.div`
  background: #0a0a0a;
  padding: 2.5rem 2rem;
  text-align: center;

  @media (max-width: 640px) {
    padding: 1.75rem 1.25rem;
  }
`;

const ReptileLogo = styled.img`
  max-width: min(520px, 100%);
  height: auto;
  display: block;
  margin: 0 auto;
`;

const ReptileBody = styled.div`
  padding: 3rem;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const ReptileBadge = styled.span`
  display: inline-block;
  background: linear-gradient(90deg, #6bb872, #5baed7);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.35rem 0.9rem;
  border-radius: 50px;
  margin-bottom: 1.25rem;
`;

const ReptileTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  color: ${props => props.theme.colors.primary};
  line-height: 1.2;
  margin-bottom: 1rem;
`;

const ReptileIntro = styled.p`
  font-size: 1.05rem;
  line-height: 1.75;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1.75rem;
  max-width: 720px;
`;

const ReptileDetails = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const ReptileDetail = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.97rem;
  color: ${props => props.theme.colors.secondary};

  & svg {
    color: #5baed7;
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

const ReptileGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ReptileGalleryImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 10px;
  display: block;
`;

const ReptileComingSoon = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.secondary};
  margin: 0;
  font-style: italic;
`;

const PonyDateList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PonyDateItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.97rem;
  color: rgba(255, 255, 255, 0.9);

  & svg {
    color: ${props => props.theme.colors.accent};
    flex-shrink: 0;
  }
`;

const PonyBookButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

// ─── Map section ───────────────────────────────────────────────────────────────

const MapSection = styled.section`
  padding: 5rem 0;
  background-color: ${props => props.theme.colors.greyBackground};
`;

const MapFrame = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-top: 2.5rem;
`;

const MapImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  display: block;
`;

// ─── Pricing ───────────────────────────────────────────────────────────────────

const PricingSection = styled.section`
  padding: 5rem 0;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 3rem;
`;

const PricingCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  border: 1px solid rgba(0, 0, 0, 0.06);
  text-align: center;
`;

const PricingLabel = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.5rem;
`;

const PricingAmount = styled.div`
  font-size: 3.2rem;
  font-weight: 800;
  color: ${props => props.theme.colors.primary};
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const PricingPer = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1.25rem;
`;

const PricingNote = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.5;
  margin-bottom: 1.75rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(0,0,0,0.07);
`;

// ─── Practical info ────────────────────────────────────────────────────────────

const PracticalSection = styled.section`
  background-color: ${props => props.theme.colors.primary};
  padding: 5rem 0;
  color: white;
`;

const PracticalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const PracticalCard = styled.div`
  border-top: 3px solid ${props => props.theme.colors.accent};
  padding-top: 1.5rem;
`;

const PracticalCardTitle = styled.h3`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.75rem;
`;

const PracticalCardBody = styled.p`
  font-size: 1rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
`;

const PracticalTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  color: white;
  margin-bottom: 0.5rem;
`;

const PracticalSubtitle = styled.p`
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 560px;
`;

// ─── Component ─────────────────────────────────────────────────────────────────

const ANIMAL_PARK_BOOKING_BASE =
  '/booking-journey?locationId=978&pageId=833fe805-0f3f-4af2-a552-7e86642746ad';

const bookingUrl = (date) =>
  date ? `${ANIMAL_PARK_BOOKING_BASE}&date=${date}` : ANIMAL_PARK_BOOKING_BASE;

const BOOKING_URL = bookingUrl();

const PONY_RIDE_DATES = [
  { iso: '2026-07-12', label: 'Sunday 12 July 2026' },
  { iso: '2026-08-23', label: 'Sunday 23 August 2026' },
  { iso: '2026-09-12', label: 'Saturday 12 September 2026' },
];

const ponyRideDatesLabel = PONY_RIDE_DATES.map((d) => d.label.replace(' 2026', '')).join(', ');

const REPTILE_LOGO =
  'https://BelvoirCastlePull.b-cdn.net/Website/48455.jpg';
const REPTILE_IMAGES = [
  {
    src: 'https://BelvoirCastlePull.b-cdn.net/Website/48449.png',
    alt: 'Guest holding a snake at a Riverview Reptiles handling session',
  },
  {
    src: 'https://BelvoirCastlePull.b-cdn.net/Website/48450.png',
    alt: 'Child gently handling a bearded dragon',
  },
  {
    src: 'https://BelvoirCastlePull.b-cdn.net/Website/48448.png',
    alt: 'Families exploring the reptile display at an event gazebo',
  },
];

const experiences = [
  {
    icon: faPaw,
    title: 'Meet Our Animals',
    text: 'From woolly sheep and curious goats to free-range chickens and playful pigs — get up close with the animals that call Belvoir home.',
  },
  {
    icon: faChild,
    title: 'Feeding Sessions',
    text: 'Roll up your sleeves and feed the animals yourself. Guided feeding sessions are a highlight for children and grown-ups alike.',
  },
  {
    icon: faHorse,
    title: 'Pony Rides',
    text: `A very special treat on selected dates — ${ponyRideDatesLabel}. Our gentle ponies are ready to ride between 11am and 2pm. Add them when you book.`,
  },
  {
    icon: faLeaf,
    title: 'Estate Setting',
    text: 'Set within the historic grounds of Belvoir Castle, the Animal Park enjoys a breathtaking countryside backdrop that\'s truly like nowhere else.',
  },
];

const AnimalPark = () => {
  useRetailSection();
  const openingDisplay = getAnimalParkOpeningDisplay();

  const seoDescription =
    `Belvoir Animal Park is open every day as part of Belvoir Summer Safari. Book Summer Safari for free Animal Park entry, free ice cream and adventure playground. Pony rides on ${ponyRideDatesLabel}.`;

  const structuredDescription =
    `Belvoir Animal Park is open every day. Part of Belvoir Summer Safari — book for free Animal Park entry, free ice cream reward and adventure in the playground. Meet friendly farm animals and enjoy feeding sessions. Pony rides (11am–2pm) on ${ponyRideDatesLabel}.`;

  const ponyRideOpeningHours = PONY_RIDE_DATES.map(({ iso }) => ({
    '@type': 'OpeningHoursSpecification',
    validFrom: iso,
    validThrough: iso,
    opens: '10:00',
    closes: '16:00',
  }));

  const openingHoursSpecification = [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '10:00',
      closes: '16:00',
    },
    ...ponyRideOpeningHours,
  ];

  return (
    <Page>
      <SEO
        title="Belvoir Animal Park — Family Farm Fun at Belvoir Castle"
        description={seoDescription}
        keywords="Belvoir Animal Park, Summer Safari, farm animals, family activities, farm park, animal experiences, pony rides, Leicestershire, Belvoir Castle"
        canonicalUrl="https://www.belvoircastle.com/retail-village/animal-park"
        ogImage={animalBanner}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'TouristAttraction',
          name: 'Belvoir Animal Park',
          description: structuredDescription,
          image: animalBanner,
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Belvoir Castle',
            addressLocality: 'Grantham',
            addressRegion: 'Leicestershire',
            postalCode: 'NG32 1PE',
            addressCountry: 'UK',
          },
          openingHoursSpecification,
          priceRange: '£',
          telephone: '+441476871001',
          sameAs: [
            'https://www.facebook.com/belvoircastle',
            'https://www.instagram.com/belvoircastle',
            'https://twitter.com/belvoircastle',
          ],
        }}
      />

      <ParallaxBanner
        title="Belvoir Animal Park"
        subtitle="Open every day — part of your Belvoir Summer Safari adventure"
        image={animalBanner}
      />

      {/* Quick-info strip */}
      <InfoStrip>
        <InfoStripInner>
          <InfoItem>
            <InfoIcon><FontAwesomeIcon icon={faCalendarAlt} /></InfoIcon>
            <InfoText>
              <strong>When we&apos;re open</strong>
              <span>Open every day</span>
            </InfoText>
          </InfoItem>
          <InfoItem>
            <InfoIcon><FontAwesomeIcon icon={faClock} /></InfoIcon>
            <InfoText>
              <strong>Opening hours</strong>
              <span>{openingDisplay.times}</span>
            </InfoText>
          </InfoItem>
          <InfoItem>
            <InfoIcon><FontAwesomeIcon icon={faTicketAlt} /></InfoIcon>
            <InfoText>
              <strong>Best value</strong>
              <span>Free with Summer Safari</span>
            </InfoText>
          </InfoItem>
        </InfoStripInner>
      </InfoStrip>

      {/* Intro */}
      <IntroSection>
        <Wrap>
          <EyebrowLabel>Belvoir Castle — Retail Village</EyebrowLabel>
          <PageTitle>
            Where families fall in love<br />with farm life
          </PageTitle>
          <IntroBody>
            Nestled within the historic grounds of Belvoir Castle, our Animal Park brings
            families face to face with beautiful farm animals in a setting like no other.
            Open every day this summer as part of Belvoir Summer Safari — follow the clues,
            find the animals, meet them for real. Book Summer Safari and Animal Park entry,
            a free ice cream reward and adventure in the playground are all included.
          </IntroBody>
          <IntroCtaRow>
            <BookBtn href={SUMMER_SAFARI_URL}>
              Book Summer Safari
            </BookBtn>
            <SecondaryBookBtn href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              Animal Park only
            </SecondaryBookBtn>
          </IntroCtaRow>
        </Wrap>
      </IntroSection>

      {/* Summer Safari */}
      <SafariSection>
        <Wrap>
          <SafariPanel>
            <SafariEyebrow>13 July – 1 September 2026</SafariEyebrow>
            <SafariTitle>Part of your Summer Safari adventure</SafariTitle>
            <SafariBody>
              Belvoir Summer Safari is the perfect summer holiday day out. Follow the trail
              from the adventure playground to the Animal Park — then meet the animals for real.
              Book Summer Safari and these are included with every ticket.
            </SafariBody>
            <SafariIncludes>
              <SafariInclude>
                <FontAwesomeIcon icon={faPaw} />
                Free Animal Park entry
              </SafariInclude>
              <SafariInclude>
                <FontAwesomeIcon icon={faIceCream} />
                Free ice cream reward
              </SafariInclude>
              <SafariInclude>
                <FontAwesomeIcon icon={faTree} />
                Adventure in the playground
              </SafariInclude>
            </SafariIncludes>
            <WhiteBookBtn href={SUMMER_SAFARI_URL}>
              Book Summer Safari
            </WhiteBookBtn>
          </SafariPanel>
        </Wrap>
      </SafariSection>

      <Divider />

      {/* Experiences */}
      <ExperiencesSection>
        <Wrap>
          <SectionHeader>
            <SectionTitle>What's waiting for you</SectionTitle>
            <SectionSubtitle>
              Every visit to Belvoir Animal Park is packed with hands-on moments, fresh air,
              and the kind of joy that only farm animals can bring.
            </SectionSubtitle>
          </SectionHeader>
          <ExperiencesGrid>
            {experiences.map((exp, i) => (
              <ExperienceCard key={i}>
                <CardIcon>
                  <FontAwesomeIcon icon={exp.icon} />
                </CardIcon>
                <CardTitle>{exp.title}</CardTitle>
                <CardText>{exp.text}</CardText>
              </ExperienceCard>
            ))}
          </ExperiencesGrid>
        </Wrap>
      </ExperiencesSection>

      <Divider />

      {/* Pony ride days */}
      <MayFourthSection>
        <Wrap>
          <MayFourthInner>
            <MayFourthContent>
              <EventBadge>Special dates — pony rides</EventBadge>
              <MayFourthTitle>Pony rides at the Animal Park</MayFourthTitle>
              <MayFourthBody>
                Our ponies will be tacked up and ready for rides on selected dates
                throughout the summer and early autumn — a rare treat available exclusively
                on these days.
              </MayFourthBody>
              <PonyDateList>
                {PONY_RIDE_DATES.map(({ label }) => (
                  <PonyDateItem key={label}>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {label}
                  </PonyDateItem>
                ))}
              </PonyDateList>
              <MayFourthDetails>
                <MayFourthDetail>
                  <FontAwesomeIcon icon={faHorse} />
                  Pony rides available 11am – 2pm
                </MayFourthDetail>
                <MayFourthDetail>
                  <FontAwesomeIcon icon={faStar} />
                  Add pony rides when you book tickets for one of these dates
                </MayFourthDetail>
                <MayFourthDetail>
                  <FontAwesomeIcon icon={faClock} />
                  Animal Park open 10:00 – 16:00 as usual
                </MayFourthDetail>
              </MayFourthDetails>
              <PonyBookButtons>
                {PONY_RIDE_DATES.map(({ iso, label }) => (
                  <WhiteBookBtn
                    key={iso}
                    href={bookingUrl(iso)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book for {label.replace(/ \d{4}$/, '')}
                  </WhiteBookBtn>
                ))}
              </PonyBookButtons>
            </MayFourthContent>
            <MayFourthVisual>
              <MayFourthImage src={ponyImage} alt="Pony at Belvoir Animal Park" />
            </MayFourthVisual>
          </MayFourthInner>
        </Wrap>
      </MayFourthSection>

      <Divider />

      {/* Reptile Day — more dates coming soon */}
      <ReptileSection>
        <Wrap>
          <ReptileCard>
            <ReptileLogoBanner>
              <ReptileLogo
                src={REPTILE_LOGO}
                alt="Riverview Reptiles"
              />
            </ReptileLogoBanner>
            <ReptileBody>
              <ReptileBadge>Free with Animal Park admission</ReptileBadge>
              <ReptileTitle>Reptile Day with Riverview Reptiles</ReptileTitle>
              <ReptileIntro>
                We are delighted to welcome Riverview Reptiles to Belvoir Animal Park on
                selected dates throughout the season. Included at no extra charge with your
                Animal Park ticket — come face to face with fascinating reptiles in their
                own gazebo on the park.
              </ReptileIntro>
              <ReptileDetails>
                <ReptileDetail>
                  <FontAwesomeIcon icon={faDragon} />
                  Hands-on handling sessions with different reptiles
                </ReptileDetail>
                <ReptileDetail>
                  <FontAwesomeIcon icon={faTicketAlt} />
                  Free of charge — included with your Animal Park ticket
                </ReptileDetail>
              </ReptileDetails>
              <ReptileGallery>
                {REPTILE_IMAGES.map((img) => (
                  <ReptileGalleryImage key={img.src} src={img.src} alt={img.alt} />
                ))}
              </ReptileGallery>
              <ReptileComingSoon>More dates coming soon — check back for updates.</ReptileComingSoon>
            </ReptileBody>
          </ReptileCard>
        </Wrap>
      </ReptileSection>

      <Divider />

      {/* Map */}
      <MapSection>
        <Wrap>
          <SectionHeader>
            <SectionTitle>Explore the park</SectionTitle>
            <SectionSubtitle>
              Use our park map to find your favourite animals and plan your visit
              before you arrive.
            </SectionSubtitle>
          </SectionHeader>
          <MapFrame>
            <MapImage src={animalMap} alt="Belvoir Animal Park map" />
          </MapFrame>
        </Wrap>
      </MapSection>

      {/* Pricing */}
      <PricingSection>
        <Wrap>
          <SectionHeader>
            <SectionTitle>Tickets &amp; pricing</SectionTitle>
            <SectionSubtitle>
              Book Belvoir Summer Safari for the full day out — Animal Park, ice cream and
              playground included. Or book Animal Park admission on its own.
            </SectionSubtitle>
          </SectionHeader>
          <PricingGrid>
            <PricingCard>
              <PricingLabel>Summer Safari</PricingLabel>
              <PricingAmount style={{ fontSize: '2rem' }}>Best value</PricingAmount>
              <PricingPer>13 July – 1 September 2026</PricingPer>
              <PricingNote>
                Free Animal Park entry, free ice cream reward and adventure in the playground
                included with every Summer Safari ticket.
              </PricingNote>
              <BookBtn href={SUMMER_SAFARI_URL}>
                Book Summer Safari
              </BookBtn>
            </PricingCard>
            <PricingCard>
              <PricingLabel>Animal Park only</PricingLabel>
              <PricingAmount>£4.50</PricingAmount>
              <PricingPer>per person</PricingPer>
              <PricingNote>Children under 3 enter free. Tickets must be booked online in advance.</PricingNote>
              <BookBtn href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Book Tickets
              </BookBtn>
            </PricingCard>
            <PricingCard>
              <PricingLabel>Reptile Day</PricingLabel>
              <PricingAmount style={{ fontSize: '2rem' }}>Included</PricingAmount>
              <PricingPer>dates to be announced</PricingPer>
              <PricingNote>
                Riverview Reptiles visit the Animal Park at no extra cost on selected dates.
                More dates coming soon.
              </PricingNote>
              <BookBtn href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Book Animal Park
              </BookBtn>
            </PricingCard>
            <PricingCard>
              <PricingLabel>Pony rides</PricingLabel>
              <PricingAmount style={{ fontSize: '2rem' }}>Add-on</PricingAmount>
              <PricingPer>{ponyRideDatesLabel}</PricingPer>
              <PricingNote>
                Select pony rides during the online booking journey when booking your
                visit for one of the dates above. Not available on other dates.
              </PricingNote>
              <PonyBookButtons>
                {PONY_RIDE_DATES.map(({ iso, label }) => (
                  <BookBtn
                    key={iso}
                    href={bookingUrl(iso)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book for {label.replace(/ \d{4}$/, '')}
                  </BookBtn>
                ))}
              </PonyBookButtons>
            </PricingCard>
          </PricingGrid>
        </Wrap>
      </PricingSection>

      {/* Practical info */}
      <PracticalSection>
        <Wrap>
          <PracticalTitle>Plan your visit</PracticalTitle>
          <PracticalSubtitle>
            Everything you need to know before you arrive.
          </PracticalSubtitle>
          <PracticalGrid>
            <PracticalCard>
              <PracticalCardTitle>Opening days</PracticalCardTitle>
              <PracticalCardBody>
                Open every day (Mon–Sun). Part of Belvoir Summer Safari this summer.
                Pony rides are on {ponyRideDatesLabel} — book add-ons when you reserve those dates.
              </PracticalCardBody>
            </PracticalCard>
            <PracticalCard>
              <PracticalCardTitle>Opening hours</PracticalCardTitle>
              <PracticalCardBody>
                10:00 – 16:00 on all open days. Last entry is at 15:30. We recommend
                arriving early to make the most of your visit.
              </PracticalCardBody>
            </PracticalCard>
            <PracticalCard>
              <PracticalCardTitle>Getting here</PracticalCardTitle>
              <PracticalCardBody>
                Belvoir Castle, Grantham, Leicestershire, NG32&nbsp;1PE.
                Free parking is available on site. Follow signs for the Retail Village.
              </PracticalCardBody>
            </PracticalCard>
            <PracticalCard>
              <PracticalCardTitle>Good to know</PracticalCardTitle>
              <PracticalCardBody>
                The Animal Park is located within the Belvoir Castle Retail Village. Café,
                farm shop, and gift shop are all nearby. Wellies recommended — it's a farm!
              </PracticalCardBody>
            </PracticalCard>
          </PracticalGrid>
        </Wrap>
      </PracticalSection>

      <Footer />
    </Page>
  );
};

export default AnimalPark;
